import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import type { UserNotification } from "../types/domain";

const POLL_INTERVAL_MS = 60_000;

export function useNotifications() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const navigate = useNavigate();

  const fetchCount = useCallback(async () => {
    try {
      const { count } = await api.getUnreadCount();
      setUnreadCount(count);
    } catch {
      // silently ignore polling errors
    }
  }, []);

  useEffect(() => {
    void fetchCount();
    intervalRef.current = setInterval(
      () => void fetchCount(),
      POLL_INTERVAL_MS,
    );
    return () => clearInterval(intervalRef.current);
  }, [fetchCount]);

  const open = useCallback(async () => {
    setIsOpen(true);
    try {
      const items = await api.getNotifications();
      setNotifications(items);
    } catch {
      // ignore
    }
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      void open();
    }
  }, [isOpen, open, close]);

  const markRead = useCallback(
    async (notification: UserNotification) => {
      if (!notification.read) {
        await api.markNotificationRead(notification.id);
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, read: true } : n,
          ),
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      }
      close();
      navigate(notification.redirectUrl);
    },
    [close, navigate],
  );

  const markAllRead = useCallback(async () => {
    await api.markAllNotificationsRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  return {
    unreadCount,
    notifications,
    isOpen,
    toggle,
    close,
    markRead,
    markAllRead,
  };
}
